import classnames from 'classnames';
import type * as React from 'react';

import { ErrorIcon } from '../Icon';
import Tooltip from '../Tooltip';
import './Checkbox.css';

interface CheckboxProps {
  /**
   * Unique ID for the checkbox
   */
  id: string;
  /**
   * Name attribute for the checkbox
   */
  name: string;
  /**
   * Label text for the checkbox
   */
  label: string;
  /**
   * Value of the checkbox
   */
  value: string;
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
  /**
   * Callback when checkbox value changes
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Additional class name
   */
  className?: string;
  /**
   * Error message to display with an error icon and tooltip
   */
  errorMessage?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

function Checkbox({
  id,
  name,
  label,
  value,
  checked = false,
  disabled = false,
  onChange,
  className,
  errorMessage,
  'data-testid': dataTestId,
}: CheckboxProps) {
  const checkboxClasses = classnames('access-checkbox', className, {
    'access-checkbox--checked': checked,
    'access-checkbox--disabled': disabled,
    'access-checkbox--error': !!errorMessage,
  });
  const baseTestId = dataTestId ?? 'checkbox';

  const errorId = `${id}-error`;

  return (
    <div
      className='access-checkbox__wrapper'
      data-testid={baseTestId}>
      <label
        className={checkboxClasses}
        data-testid={`${baseTestId}-label`}
        htmlFor={id}>
        {/* aria-describedby has to sit on the control: assistive tech reads a
            description from the element it is on, so on the <label> it was never
            announced and the error was not associated with the checkbox. */}
        <input
          aria-describedby={errorMessage ? errorId : undefined}
          aria-invalid={!!errorMessage}
          checked={checked}
          className='access-checkbox__input'
          data-testid={`${baseTestId}-input`}
          disabled={disabled}
          id={id}
          name={name}
          readOnly={!onChange}
          type='checkbox'
          value={value}
          onChange={onChange}
        />
        <span
          className='access-checkbox__label'
          data-testid={`${baseTestId}-label-text`}>
          {label}
        </span>
      </label>
      {errorMessage ? (
        <>
          <span
            className='sr-only'
            id={errorId}>
            {errorMessage}
          </span>
          <Tooltip
            isError
            activeClassName='access-checkbox__error-tooltip--active'
            className='access-checkbox__error-icon'
            placement='top'
            title={errorMessage}>
            <span
              className='access-checkbox__error-icon-content'
              role='img'>
              <ErrorIcon ariaLabel={`Error: ${errorMessage}`} />
            </span>
          </Tooltip>
        </>
      ) : (
        <span
          aria-hidden='true'
          className='access-checkbox__error-icon-placeholder'
        />
      )}
    </div>
  );
}

export default Checkbox;
